import { toast } from "@gigpro/ui/Toast";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useAdminPro } from "api/queries/admin";
import { useToggleFavorite } from "api/queries/findAPro";
import { marketsQueryOptions } from "api/queries/markets";
import { useProProfile } from "api/queries/pros";
import type { ProBadge } from "api/resources/pros";
import { DeleteConfirmationDialog } from "components/DeleteConfirmationDialog";
import { InviteToJobButton } from "domains/hire/containers/InviteToJobButton";
import { PROfileHeader, PROfileHeaderSkeleton } from "domains/pros/components/PROfileHeader";
import { StartOnboardingDialogue } from "domains/pros/components/StartOnboardingDialogue";
import { useMessagePro } from "domains/pros/hooks/useMessagePro";
import { getFullName } from "domains/pros/utils/getFullName";
import React from "react";
import { useActiveLocationId } from "services/locationOrgContext";
import * as z from "zod/mini";
import { SendDirectOfferContainer } from "../SendDirectOfferDialog";
import { directOfferSearchSchema, type OfferType } from "../SendDirectOfferDialog/routing";

type PROfileHeaderContainerProps = { id: number; preview?: boolean; admin?: boolean };

export const PROfileHeaderContainer: React.FC<PROfileHeaderContainerProps> = ({ id, preview, admin }) => {
  const [memberToUnFavorite, setMemberToUnFavorite] = React.useState<number>();
  const navigate = useNavigate();
  const { offerType } = z.catch(directOfferSearchSchema, {}).parse(useSearch({ strict: false }));
  const setOfferType = (next: OfferType | undefined) =>
    navigate({ to: ".", search: (prev) => ({ ...prev, offerType: next }), replace: true });
  const [onboardingDialogOpen, setOnboardingDialogOpen] = React.useState(false);
  const [activeLocationId] = useActiveLocationId();

  const { messagePro, isLoading: messagingLoading } = useMessagePro();

  const markets = useQuery(marketsQueryOptions());
  const { data, refetch } = useProProfile(id);
  const { data: proAdminData } = useAdminPro({ id });

  const market = markets.data?.find((market) => market.id === data?.market_id);
  const badges = proAdminData?.gigster_profile?.badges ?? [];

  const toggleFavorite = useToggleFavorite({ onSuccess: () => refetch() });

  const removeFavorite = async (favoriteId: number) => {
    setMemberToUnFavorite(undefined);
    const toastId = toast.loading("Removing favorite...");
    try {
      await toggleFavorite.mutateAsync({ favorite: false, favoriteId });
      toast.success("Favorite removed!", { id: toastId });
    } catch {
      toast.error("Failed to remove favorite.", { id: toastId });
    }
  };
  const addingMemberAsFavorite = async (memberId: number) => {
    const toastId = toast.loading("Adding favorite...");
    try {
      if (activeLocationId) {
        await toggleFavorite.mutateAsync({ favorite: true, locationId: activeLocationId, userId: memberId });
      }
      toast.success("Favorite added!", { id: toastId });
    } catch {
      toast.error("Failed to add favorite.", { id: toastId });
    }
  };

  const handleSendOffer = () => {
    if (preview) {
      setOnboardingDialogOpen(true);
      return;
    }
    void setOfferType("select");
  };

  const handleOnMessage = async () => {
    if (preview) {
      setOnboardingDialogOpen(true);
      return;
    }
    await messagePro(id);
  };

  const proName = getFullName({
    first_name: proAdminData?.first_name,
    last_name: proAdminData?.last_name,
    display_name: data?.display_name,
  });

  const adminFullName = admin && proAdminData ? getFullName(proAdminData) || undefined : undefined;

  if (!data) return <PROfileHeaderSkeleton />;

  return (
    <>
      <PROfileHeader
        data={{
          ...data,
          badges,
          phone_number: proAdminData?.phone_number ?? undefined,
          in_network: !!proAdminData?.in_network,
          market_name: market?.name,
        }}
        onSendOffer={handleSendOffer}
        onMessage={handleOnMessage}
        messagingLoading={messagingLoading}
        onFavoriteMember={addingMemberAsFavorite}
        onUnFavoriteMember={setMemberToUnFavorite}
        hideButtons={admin}
        hideFavorite={preview || admin}
        showAdminData={admin}
        fullName={adminFullName}
        extraButtons={<InviteToJobButton proId={id} />}
      />
      <SendDirectOfferContainer
        pro={{
          display_name: data.display_name,
          user_id: id,
          profile_id: data.id,
          bio: data.bio,
          headshot_public_id: data.headshot_public_id,
          badges: data.badges.filter((badge): badge is ProBadge[number] => badge !== "TOP_PRO"),
          supported_employment_types: [...data.supported_employment_types],
          pro_preferences: {
            pro_contractor_type: data.pro_preferences?.pro_contractor_type ?? [],
          },
        }}
        offerType={offerType}
        setOfferType={setOfferType}
      />
      <DeleteConfirmationDialog
        onClose={() => setMemberToUnFavorite(undefined)}
        onDelete={() => memberToUnFavorite && removeFavorite(memberToUnFavorite)}
        open={!!memberToUnFavorite}
        content="This Pro will stop being a favorite and will be removed from all groups at the current location."
        deleteLabel="Remove Favorite"
      />
      <StartOnboardingDialogue
        open={onboardingDialogOpen}
        avatarUrl={data.headshot_public_id}
        proName={proName}
        onClose={() => setOnboardingDialogOpen(false)}
      />
    </>
  );
};
